// ============================================================
// RECONCILE - loading a map mid-fight without losing the fight.
//
// Swapping the terrain is one write. Tokens are separate documents, so
// wounds, conditions and initiative survive BY CONSTRUCTION - there is
// no code here that could lose them.
//
// What does need answering is where everyone is standing afterwards. The
// rule is: KEEP POSITIONS, LIFT AND FLAG. Nobody teleports, because the
// GM placed them where they are and the new map is usually the same
// fight seen properly. Anyone who now finds themselves inside a wall is
// stood on top of it and named, so the GM can move them deliberately
// rather than discovering it three rounds later.
//
// WHAT IS OVERHEAD IS NOT A FLOOR (v7.43.0). Terrain whose base is at or
// below someone's feet still carries them up, however tall - that is the
// ground rising under them, and it is what building a wall where they
// stand means. A slab hanging in the air above their head is a ceiling,
// and walking beneath it leaves them where they are.
//
// Pure. Zero React dependency.
// ============================================================
import type { Solid } from './types';
import type { Boundary } from './boundary';
import { clampToBoundary, boundaryContains } from './boundary';
import { solidGeometry, solidBounds } from './solids';
import { containsPoint } from './lineOfSight';
import type { Token } from './tokens';
import { objectSolidId, withoutOwnBox } from './ownBox';

export interface Reconciliation {
  tokens: Record<string, Token>;
  /** Ids of tokens that were moved or lifted, for the log. */
  lifted: string[];
  movedInside: string[];
}

/**
 * A stride, in metres - roughly a third of an average person's height,
 * the same fraction `traitRules.maxStepForSize` uses.
 */
const DEFAULT_STEP = 0.6;

/** A surface at a column: the top you would stand on, and where it starts. */
export interface Surface {
  top: number;
  base: number;
  /** The solid this is the top of. Absent on the ground itself. */
  solidId?: string;
  /** What its author called it, if anything. */
  label?: string;
}

/** Where somebody is standing, and how far up they can step from there. */
export interface Footing {
  /** The height they are at now. */
  from?: number;
  /** How far above that still counts as a step rather than a climb. */
  maxStep?: number;
}

/**
 * The highest surface at this ground point that can actually be REACHED.
 *
 * `floor` is where the ground itself is - zero on undisturbed land, and
 * negative inside a trench. Solids stack up from there.
 *
 * A FLOATING SLAB IS A CEILING, NOT A FLOOR. Without a footing this
 * returns the top of the highest solid over the point, with no notion of
 * whether anyone could get there - so a gantry and a doorstep look
 * identical, and walking under a platform snapped you onto its roof.
 *
 * With no footing the old answer comes back exactly, which is what every
 * caller that has not been given one still relies on.
 */
export function surfaceHeightAt(x: number, z: number, solids: Solid[], floor = 0, footing: Footing = {}): number {
  return reachableSurface(surfaceCandidatesAt(x, z, solids, floor), footing);
}

/**
 * EVERY surface at this ground point, lowest first.
 *
 * The ground itself, then the top of each solid standing over it. WHICH
 * of them is a floor depends on who is asking and where they are, so
 * that question is answered separately - which lets the expensive half
 * be worked out once per column and reused, however many times it is
 * asked from different heights.
 */
export function surfaceCandidatesAt(x: number, z: number, solids: readonly Solid[], floor = 0): Surface[] {
  const tops: Surface[] = [{ top: floor, base: floor }];
  for (const solid of solids) {
    const surface = surfaceOf(solid, x, z);
    if (surface) tops.push(surface);
  }
  return tops.sort((a, b) => a.top - b.top);
}

/**
 * The top of this solid as something to stand on at (x, z), or null.
 *
 * YOU DO NOT STAND ON SMOKE. Anything you can walk into is something you
 * are inside rather than on top of, so it offers no surface - which is
 * what stops a marker being lifted onto a forest canopy. And you do not
 * stand ON paint: a flat solid is a ground marking, not a platform
 * (v8.199.20).
 *
 * Bounds first: six comparisons rejects almost every solid on a map of
 * any size, and both the bounds and the geometry are memoised. Then a
 * point just inside the top: cheap and exact enough for a prism, and it
 * correctly ignores a cone's overhang.
 *
 * Named here rather than by a second scan of the same column: what a
 * surface IS and what it is CALLED are one fact, and the level picker
 * needs both to say "Gantry - 4.4 m" instead of a bare number.
 */
function surfaceOf(solid: Solid, x: number, z: number): Surface | null {
  if (solid.permeability || solid.flat) return null;
  const box = solidBounds(solid);
  if (x < box.min.x || x > box.max.x || z < box.min.z || z > box.max.z) return null;
  if (!containsPoint(solidGeometry(solid), { x, y: box.max.y - 1e-6, z })) return null;
  return { top: box.max.y, base: box.min.y, solidId: solid.id, ...(solid.label ? { label: solid.label } : {}) };
}

/**
 * The highest of those surfaces that can be reached from `footing`.
 *
 * A crate is a step; a gantry four metres up is a ceiling - until you are
 * standing on it, when it becomes the floor. With no footing every
 * surface is reachable, which is the answer this gave before v7.43.0 and
 * which every caller that has not been told otherwise still expects.
 */
export function reachableSurface(tops: Surface[], footing: Footing = {}): number {
  const from = footing.from;
  const reach = footing.maxStep !== undefined && from !== undefined ? from + footing.maxStep : Infinity;
  let highest = tops[0]?.top ?? 0;
  for (const surface of tops) {
    // TWO WAYS ONTO SOMETHING, and they are not the same event.
    //
    // You STEP UP onto anything whose top is within a stride. And you are
    // CARRIED UP by anything whose base is at or below your feet, however
    // tall it is - that is the ground rising under you, which is what
    // building a platform where somebody is standing does.
    //
    // What is left is the case this rule exists for: a slab whose base is
    // above your head and whose top is out of reach. That is a ceiling,
    // and walking under it must not put you on top of it.
    const step = surface.top <= reach + 1e-9;
    const underfoot = from === undefined || surface.base <= from + 1e-9;
    if (step || underfoot) highest = Math.max(highest, surface.top);
  }
  return highest;
}

/**
 * The surface you end up on ENTERING this column from height `from`.
 *
 * The rule the flood fill needed and never had. It asked
 * `reachableSurface` with no footing, which answers "the highest thing
 * here" - so walking beneath a gantry put a marker on its roof, and the
 * only defence against that was a height constant calling anything tall
 * `impassable`, which then made the roof of every building unwalkable.
 *
 * ONE QUESTION DECIDES IT: is the thing in your WAY? A solid whose base
 * is at or below your feet and whose top is above them blocks the column
 * at the height you are crossing it, and the only way on is over the top
 * of it - a kerb and a five-metre wall are the same event at different
 * scales, and what each COSTS is the movement rules' business, not this
 * function's. Anything whose base is above your head is a ceiling you
 * walk under. Anything wholly below your feet is something you drop
 * onto, the highest one first.
 *
 * Pure geometry: no budget, no traits, no opinion about whether the
 * climb is affordable.
 */
export function arrivalSurface(tops: Surface[], from: number): number {
  const at = climbUntilClear(tops, from);
  if (at > from) return at;
  // Nothing was in the way: drop to the highest thing under your feet.
  const below = highestTop(tops.filter((surface) => surface.top <= from + 1e-9));
  return below > -Infinity ? below : from;
}

/**
 * CLIMB UNTIL CLEAR, not once. Topping out on a wall can leave you
 * inside a parapet that straddles the height you just reached, and one
 * pass would stop there. Bounded by the number of surfaces, because each
 * round settles on a strictly higher top.
 */
function climbUntilClear(tops: Surface[], from: number): number {
  let at = from;
  for (let pass = 0; pass <= tops.length; pass++) {
    const overTop = highestTop(tops.filter((s) => s.base <= at + 1e-9 && s.top > at + 1e-9));
    if (overTop === -Infinity) break;
    at = overTop;
  }
  return at;
}

/** The highest top among these surfaces, or -Infinity for none. */
function highestTop(surfaces: Surface[]): number {
  return surfaces.reduce((best, s) => Math.max(best, s.top), -Infinity);
}

export interface VerticalOptions {
  /** Passes through solid matter - Phase and Incorporeal, never Flyer. */
  phases?: boolean;
  /** Where the ground itself is; negative inside a trench. */
  floor?: number;
}

/**
 * How far somebody at this column can actually move up or down.
 *
 * STRUCTURES ARE FLOORS AND CEILINGS, not suggestions. Raising a marker
 * with the wheel used to pass straight through a gantry, which made
 * "under it" and "on top of it" a matter of how far the wheel had been
 * turned rather than of what was there.
 *
 *   rising  → stops at the BASE of the lowest thing overhead
 *   falling → stops at the TOP of the highest thing below
 *
 * Standing on a slab, you cannot sink into it: to come down you walk off
 * the edge, which is where a fall would happen if falling is ever added.
 *
 * FLYING IS NOT PHASING. A flyer chooses their altitude and still meets
 * the underside of a gantry; only Phase and Incorporeal pass through, and
 * `traitRules`' `FLIES` set - which lumps all three together for
 * horizontal terrain - is deliberately not the set consulted here.
 */
export function verticalStop(
  x: number,
  z: number,
  fromY: number,
  toY: number,
  solids: Solid[],
  options: VerticalOptions = {},
): number {
  if (options.phases || toY === fromY) return toY;
  const slabs = surfaceCandidatesAt(x, z, solids, options.floor ?? 0);
  if (toY > fromY) return Math.min(toY, ceilingAbove(slabs, fromY));
  // Coming down: the highest top at or below where they are.
  const ground = slabs.reduce((g, slab) => (slab.top <= fromY + 1e-6 && slab.top > g ? slab.top : g), -Infinity);
  return Math.max(toY, ground === -Infinity ? toY : ground);
}

/**
 * The nearest underside strictly above where the feet are now. A slab
 * they are already standing on has its base below them and is not a
 * ceiling to them.
 */
function ceilingAbove(slabs: Surface[], fromY: number): number {
  return slabs.reduce((low, slab) => (slab.base > fromY + 1e-6 && slab.base < low ? slab.base : low), Infinity);
}

/**
 * Settle every token onto the new terrain.
 *
 * Positions are kept; only height changes, and only when the ground
 * moved under someone. A token outside the new arena is pulled to its
 * edge - the alternative is a combatant nobody can see or select.
 */
export function reconcileTokens(
  tokens: Record<string, Token>,
  boundary: Boundary,
  solids: Solid[],
  /** Where the ground itself is - negative inside a trench. */
  groundAt: (x: number, z: number) => number = () => 0,
  /**
   * How far anyone can step up while settling.
   *
   * A single stride for everybody, rather than each combatant's own -
   * settling runs from the sync layer, which does not know anyone's
   * traits, and the answer only decides whether a NEW piece of terrain
   * picks somebody up. A deliberate move uses the mover's own reach
   * (see `useTokenDrag`).
   */
  footing: Footing = { maxStep: DEFAULT_STEP },
): Reconciliation {
  const next: Record<string, Token> = {};
  const lifted: string[] = [];
  const movedInside: string[] = [];
  // The solids' ids once, so a token's own box is a lookup rather than a scan of every solid per token.
  const ground: Ground = { boundary, solids, ids: new Set(solids.map((s) => s.id)), groundAt, footing };
  for (const [id, token] of Object.entries(tokens)) {
    const settled = settleToken(token, ground);
    next[id] = settled.token;
    if (settled.clamped) movedInside.push(id);
    if (settled.lifted) lifted.push(id);
  }
  return { tokens: next, lifted, movedInside };
}

/** Everything a token settles against. */
interface Ground {
  boundary: Boundary;
  solids: Solid[];
  ids: Set<string | undefined>;
  groundAt: (x: number, z: number) => number;
  footing: Footing;
}

/**
 * One token, settled: pulled inside the arena, stood on what is under it.
 * The SAME token object comes back when nothing changed.
 *
 * ANYONE EXEMPT FROM TERRAIN IS EXEMPT FROM SETTLING. Building a roof
 * under a flyer must not yank it upward, and a marker deliberately placed
 * inside something must stay where it was put - it is warned, not
 * ejected (see overlap.ts).
 *
 * The floor may be BELOW zero where someone has dug, so this is not
 * clamped at zero: a token in a trench stands in the trench. FROM WHERE
 * THEY ARE, so a slab hanging over their head is a ceiling. Terrain that
 * rises from their feet still carries them up.
 *
 * NEVER ON ITS OWN BOX (2026-10-26): an Object that counts as terrain stands in its box (`ownBox.ts`), so the box is
 * skipped for it alone - everyone else meets it like any other solid.
 */
function settleToken(token: Token, ground: Ground): { token: Token; clamped: boolean; lifted: boolean } {
  if (token.ignoresTerrain) return { token, clamped: false, lifted: false };
  const { boundary, groundAt, footing } = ground;
  const solids = ground.ids.has(objectSolidId(token.id)) ? withoutOwnBox(ground.solids, token.id) : ground.solids;
  const inside = boundaryContains(boundary, token.x, token.z);
  const { x, z } = inside ? token : clampToBoundary(boundary, token.x, token.z);
  const elevation = surfaceHeightAt(x, z, solids, groundAt(x, z), { ...footing, from: token.elevation });
  const changed = !inside || Math.abs(elevation - token.elevation) > 1e-6;
  return {
    token: changed ? { ...token, x, z, elevation, rev: token.rev + 1 } : token,
    clamped: !inside,
    lifted: changed && elevation > token.elevation + 1e-6,
  };
}
